import { createCalendar } from '@internationalized/date';
import { clsx } from 'clsx';
import { motion } from 'motion/react';
import type { ComponentProps } from 'react';
import { forwardRef, useCallback, useEffect, useState } from 'react';
import type { CalendarProps as AriaCalendarProps, DateValue } from 'react-aria';
import { useCalendar } from 'react-aria';
import { useCalendarState } from 'react-stately';
import { useViewportSize } from '../../../hooks/useViewportSize';
import { spacing } from '../../../styles/spacing.css';
import { plainButton } from '../../../styles/utilities.css';
import { Button } from '../../Button/Button';
import { Flex } from '../../Flex/Flex';
import { Icon } from '../../Icon/Icon';
import { Layout } from '../../Layout/Layout';
import { MIN_NUMBER_OF_VISIBLE_YEARS_FOR_DESKTOP } from '../constants';
import type { MobileButtonsText } from '../DatePicker';
import * as styles from './Calendar.css';
import { CalendarHeader } from './CalendarHeader/CalendarHeader';
import { Days } from './CalendarViews/Days';
import { MonthsYearsView } from './CalendarViews/MonthsYearsView';
import { useManualCalendar } from './hooks/useManualCalendar';
import type { SideBySideView } from './types';
import { CalendarView } from './types';

const variants = {
  hiddenState: { opacity: 0 },
  animate: { opacity: 1 },
};

export interface CalendarProps extends AriaCalendarProps<DateValue>, Pick<ComponentProps<'div'>, 'className'> {
  readonly calendarView: CalendarView;
  readonly setCalendarView: (step: CalendarView) => void;
  readonly sideBySideCalendarView?: SideBySideView;
  readonly mobileButtonsText?: MobileButtonsText;
  readonly close?: () => void;
  readonly noValue?: boolean;
  readonly locale?: string;
  readonly showMobileKeyboard?: () => void;
}

export const Calendar = forwardRef<HTMLDivElement, CalendarProps>(
  (
    {
      className,
      calendarView,
      setCalendarView,
      sideBySideCalendarView,
      onChange,
      mobileButtonsText,
      showMobileKeyboard,
      close,
      noValue = false,
      locale = 'en-US',
      ...props
    },
    ref,
  ) => {
    const { isBelowDesktopViewport, isMobileViewport } = useViewportSize();

    const shouldShowSideBySideCalendarView =
      typeof sideBySideCalendarView === 'boolean' ? sideBySideCalendarView : sideBySideCalendarView != null;
    const [tempSelection, setTempSelection] = useState<DateValue | null>(props.value ?? null);
    const isDoneButtonDisabled = shouldShowSideBySideCalendarView ? tempSelection === null : noValue;

    useEffect(() => {
      if (shouldShowSideBySideCalendarView) {
        setTempSelection(props.value ?? null);
      }
    }, [props.value, shouldShowSideBySideCalendarView]);

    const handleCalendarChange = useCallback(
      (date: DateValue | null) => {
        if (shouldShowSideBySideCalendarView && date != null) {
          setTempSelection(date);
        }

        if (!shouldShowSideBySideCalendarView && date != null) {
          onChange?.(date);
        }
      },
      [shouldShowSideBySideCalendarView, onChange],
    );

    const state = useCalendarState({
      ...props,
      value: shouldShowSideBySideCalendarView ? tempSelection : props.value,
      autoFocus: false,
      onChange: handleCalendarChange,
      locale,
      createCalendar,
    });
    const { calendarProps, title, prevButtonProps, nextButtonProps } = useCalendar(props, state);

    const handleDoneClick = useCallback(() => {
      // istanbul ignore else -- @preserve - the else path won't be taken since the button is disabled when tempSelection is null
      if (tempSelection) {
        onChange?.(tempSelection);
        close?.();
      }
    }, [onChange, tempSelection, close]);

    const { years, months, daysView, onYearChange, onMonthChange, isYearSelected, isMonthSelected } = useManualCalendar(
      {
        state,
        calendarView,
        setCalendarView,
        isSideBySideCalendarView: sideBySideCalendarView != null,
        minNumberOfVisibleYears: isMobileViewport ? undefined : MIN_NUMBER_OF_VISIBLE_YEARS_FOR_DESKTOP,
      },
    );

    const onClick = useCallback(() => setCalendarView(CalendarView.Day), [setCalendarView]);

    return (
      <>
        <Flex
          {...calendarProps}
          className={clsx(styles.calendar, className)}
          flex="1"
          flexDirection="column"
          flexShrink="0"
          overflow="hidden"
          ref={ref}
        >
          {isBelowDesktopViewport && (
            <Flex
              alignItems="center"
              alignSelf="flex-end"
              as="button"
              className={plainButton}
              height={spacing.s40}
              justifyContent="center"
              marginBlockStart={{ md: spacing.s16 }}
              marginInlineEnd={{ md: spacing.s16 }}
              onClick={showMobileKeyboard}
              onKeyDown={showMobileKeyboard}
              type="button"
              width={spacing.s40}
            >
              <Icon name="keyboard" size="lg" />
            </Flex>
          )}

          <CalendarHeader
            daysView={daysView}
            isNextDisabled={nextButtonProps.isDisabled}
            isPrevDisabled={prevButtonProps.isDisabled}
            setCalendarView={setCalendarView}
            state={state}
            title={title}
          />
          <Layout
            animate="animate"
            as={motion.div}
            exit="hiddenState"
            initial="hiddenState"
            key={calendarView}
            minWidth={{ lg: styles.DESKTOP_MIN_WIDTH }}
            transition={{ duration: 0.5 }}
            variants={variants}
          >
            {calendarView === CalendarView.Day ? (
              <Days locale={locale} state={state} />
            ) : (
              <MonthsYearsView
                calendarView={calendarView}
                isMonthSelected={isMonthSelected}
                isSideBySideView={sideBySideCalendarView}
                isYearSelected={isYearSelected}
                months={months}
                onMonthChange={onMonthChange}
                onYearChange={onYearChange}
                years={years}
              />
            )}
          </Layout>
        </Flex>
        {shouldShowSideBySideCalendarView &&
          (calendarView === CalendarView.Day ? (
            <Button aria-label="Done" disabled={isDoneButtonDisabled} onClick={handleDoneClick} variant="secondary">
              {mobileButtonsText?.done ?? 'Done'}
            </Button>
          ) : (
            <Button aria-label="Go to date" onClick={onClick} variant="secondary">
              {mobileButtonsText?.goToDate ?? 'Go to date'}
            </Button>
          ))}
      </>
    );
  },
);
